import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray, useWatch, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { salesDocumentSchema, type SalesDocumentFormData } from './sales-schema';
import {
  calculateDocumentTotals,
  calculateLineTotals,
  calculateSalesDocumentBenefit,
  calculateSalesLineBenefit,
  netUnitCostHt,
} from '@erp/domain/sales';
import { useClients } from '@/hooks/use-clients';
import { useArticles } from '@/hooks/use-articles';
import { useBarcodeScanner } from '@/hooks/use-barcode-scanner';
import { useCreateSalesDocument, useUpdateSalesDocument, useMarkAsInvoiced } from '@/hooks/use-sales';
import { useUpdateClient } from '@/hooks/use-clients';
import { useCompanySettings } from '@/hooks/use-settings';
import { useWarehouses, useCreateStockMovement } from '@/hooks/use-stock';
import { useCreatePayment, useDocumentPayments } from '@/hooks/use-payments';
import { computePaymentStatus, sumAllocations, type PaymentStatus } from '@erp/domain/payments';
import { PaymentStatusBadge } from '@/components/PaymentStatusBadge';
import { AddPaymentDialog } from './AddPaymentDialog';
import { ClientPickerDialog } from './ClientPickerDialog';
import { ArticlePickerDialog, type ArticleWithQty } from './ArticlePickerDialog';
import { resolveDocumentCustomer } from './document-customer';
import { RepresentativeSelect } from './RepresentativeSelect';
import { useClientRepresentatives } from '@/hooks/use-representatives';
import { ArticleFormDialog } from '../articles/ArticleFormDialog';
import type { SalesDocument, SalesDocType, Client, Article } from '@erp/data-access/interfaces';
import {
  Badge, Button, Card, CardContent, DatePicker, Input, Label, Separator, Textarea,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@erp/ui';
import { Plus, Minus, Trash2, Search, PackageSearch, Eye } from '@/components/icons';
import {
  useColumnConfig,
  ColumnConfigDialog,
  ColumnConfigButton,
  type ColDef,
} from '@/components/ui/column-config';

const VAT_RATES = [19, 13, 7, 0] as const;
const INTEGER_UNITS = new Set(['pcs', 'box']);
const STAMP_AMOUNT = 1; // Timbre fiscal standard tunisien : 1 DT par document

const SALES_LINE_COL_DEFS: ColDef[] = [
  { key: 'lineNumber',   labelKey: 'sales.lineNumber' },
  { key: 'articleCode',  labelKey: 'sales.articleCode' },
  { key: 'description', labelKey: 'sales.description', locked: true },
  { key: 'quantity',    labelKey: 'sales.quantity' },
  { key: 'unit',         labelKey: 'sales.unit' },
  { key: 'unitPrice',   labelKey: 'sales.unitPrice' },
  { key: 'unitPriceTtc', labelKey: 'sales.unitPriceTtc' },
  { key: 'purchasePrice', labelKey: 'sales.purchasePrice' },
  { key: 'discount',    labelKey: 'sales.discount' },
  { key: 'vatRate',     labelKey: 'sales.vatRate' },
  { key: 'lineTotal',   labelKey: 'sales.lineTotal' },
  { key: 'lineTotalTtc', labelKey: 'sales.lineTotalTtc' },
  { key: 'benefit',     labelKey: 'sales.benefit' },
];
const SALES_COL_WIDTH: Record<string, string> = {
  lineNumber:   '0.6fr',
  articleCode:  '1fr',
  description: '3fr',
  quantity:    '1.5fr',
  unit:         '0.8fr',
  unitPrice:   '1fr',
  unitPriceTtc: '1fr',
  purchasePrice: '1fr',
  discount:    '0.8fr',
  vatRate:     '0.8fr',
  lineTotal:   '1fr',
  lineTotalTtc: '1fr',
  benefit:     '1fr',
};
const SALES_DEFAULT_LINE_COLS = ['description', 'quantity', 'unitPrice', 'discount', 'vatRate', 'lineTotal', 'benefit'];

function formatPrice(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function getQtyStep(unit: string): string {
  return INTEGER_UNITS.has(unit) ? '1' : '0.001';
}

interface SalesDocumentFormProps {
  docType: SalesDocType;
  document?: SalesDocument | null;
  onSaved: () => void;
  onCancel: () => void;
}

export function SalesDocumentForm({ docType, document, onSaved, onCancel }: SalesDocumentFormProps) {
  const { t } = useTranslation();
  const isEdit = !!document?.id;
  const isFiscalStampRequired = docType === 'invoice';
  const { data: clients } = useClients();
  const { data: articles } = useArticles({ isActive: true, limit: 1000 });
  const createMutation = useCreateSalesDocument();
  const updateMutation = useUpdateSalesDocument();
  const updateClientMutation = useUpdateClient();
  const markInvoicedMutation = useMarkAsInvoiced();
  const { data: companySettings } = useCompanySettings();

  const { data: warehouses } = useWarehouses();
  const stockMovementMutation = useCreateStockMovement();
  const createPaymentMutation = useCreatePayment();
  const { data: docPayments } = useDocumentPayments(
    isEdit && docType === 'delivery' ? document?.id : undefined,
    'delivery',
  );
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [articlePickerOpen, setArticlePickerOpen] = useState<number | 'new' | null>(null);
  const [viewingArticleId, setViewingArticleId] = useState<string | null>(null);
  const [lineColDialogOpen, setLineColDialogOpen] = useState(false);

  const lineColCfg = useColumnConfig({
    storageKey: 'erp_sales_doc_lines_columns',
    allColumns: SALES_LINE_COL_DEFS,
    defaultColumns: SALES_DEFAULT_LINE_COLS,
  });
  const lineGridStyle = {
    gridTemplateColumns: lineColCfg.visibleCols.map((k) => SALES_COL_WIDTH[k] ?? '1fr').join(' ') + ' auto',
  } as const;

  // Article lookup map — always fresh from TanStack Query cache
  const articleMap = useMemo(() => {
    const map = new Map<string, Article>();
    articles?.forEach((a) => map.set(a.id, a));
    return map;
  }, [articles]);

  const defaultLine = { articleId: '', description: '', quantity: 1, unitPriceHt: 0, discountPct: 0, vatRate: 19, unit: 'pcs', purchasePrice: 0 };

  const defaultClient = useMemo(() => clients?.find((c) => c.isDefault), [clients]);

  const initialValues = useMemo<SalesDocumentFormData>(() => {
    if (!document) {
      return { clientId: defaultClient?.id ?? '', representativeId: '', carRegistrationNumber: defaultClient?.carRegistrationNumber ?? '', docDate: todayISO(), validityDate: '', parentDocId: '', notes: '', stampApplied: isFiscalStampRequired, lines: [], paymentChoice: 'paid', paymentMethod: 'cash', paidAmount: 0 };
    }
    return {
      clientId: document.clientId,
      representativeId: document.representativeId ?? '',
      carRegistrationNumber: document.carRegistrationNumber ?? '',
      docDate: document.docDate,
      validityDate: document.validityDate ?? '',
      parentDocId: document.parentDocId ?? '',
      notes: document.notes ?? '',
      stampApplied: isFiscalStampRequired || (document.stampAmount ?? 0) > 0,
      lines: (document.lines && document.lines.length > 0)
        ? document.lines.map((l) => ({
            articleId: l.articleId ?? '',
            description: l.description,
            quantity: l.quantity,
            unitPriceHt: l.unitPriceHt,
            discountPct: l.discountPct,
            vatRate: l.vatRate,
            unit: 'pcs',
            purchasePrice: l.purchasePrice ?? 0,
          }))
        : [],
      paymentChoice: 'paid',
      paymentMethod: 'cash',
      paidAmount: 0,
    };
  }, [document, defaultClient, isFiscalStampRequired]);

  const {
    register, handleSubmit, control, setValue, setError,
    formState: { errors, isSubmitting },
  } = useForm<SalesDocumentFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Zod v4 + RHF resolver compat
    resolver: zodResolver(salesDocumentSchema) as any,
    defaultValues: initialValues,
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'lines' });

  const watchedLines = useWatch({ control, name: 'lines' });
  const watchedClientId = useWatch({ control, name: 'clientId' });
  const watchedCarRegistrationNumber = useWatch({ control, name: 'carRegistrationNumber' });
  const watchedRepId = useWatch({ control, name: 'representativeId' });
  const watchedPaymentChoice = useWatch({ control, name: 'paymentChoice' });
  const watchedStampApplied = useWatch({ control, name: 'stampApplied' });

  const isExternalCustomer = document?.customerSource === 'pro'
    || document?.customerSource === 'online'
    || Boolean(document?.proCustomerId)
    || Boolean(document?.onlineCustomerId && !document?.clientId);
  const selectedClient = isExternalCustomer ? undefined : clients?.find((c: Client) => c.id === watchedClientId);
  const documentCustomer = resolveDocumentCustomer(document, clients);
  const { data: clientReps } = useClientRepresentatives(watchedClientId || '');
  const preferredRepIds = useMemo(() => (clientReps ?? []).map((r) => r.id), [clientReps]);

  // Auto-select default client for new documents
  useEffect(() => {
    if (!document && defaultClient && !watchedClientId) {
      setValue('clientId', defaultClient.id);
      setValue('carRegistrationNumber', defaultClient.carRegistrationNumber ?? '');
    }
  }, [defaultClient, document, setValue, watchedClientId]);

  useEffect(() => {
    if (!selectedClient) return;
    if (document?.clientId === selectedClient.id && document.carRegistrationNumber) return;
    setValue('carRegistrationNumber', selectedClient.carRegistrationNumber ?? '');
  }, [document?.clientId, document?.carRegistrationNumber, selectedClient, setValue]);

  // Net unit cost (HT, after supplier discount) for a line, frozen at sale time.
  // Priority: the line's snapshot (captured when the article was picked, or
  // loaded from the saved document), then the catalog as a fallback. The
  // catalog only fills a missing snapshot — it never overrides a frozen one.
  const getLineNetCost = (index: number): number => {
    const line = watchedLines?.[index];
    const snapshot = Number(line?.purchasePrice);
    if (Number.isFinite(snapshot) && snapshot > 0) return snapshot;
    const articleId = line?.articleId;
    if (articleId) {
      const catalogCost = netUnitCostHt(articleMap.get(articleId));
      if (catalogCost != null) return catalogCost;
      const storedLine = document?.lines?.find((candidate) => candidate.articleId === articleId);
      const stored = Number(storedLine?.purchasePrice);
      if (Number.isFinite(stored) && stored > 0) return stored;
    }
    return 0;
  };

  const getLineUnit = (index: number): string => {
    const articleId = watchedLines?.[index]?.articleId;
    if (articleId) {
      const article = articleMap.get(articleId);
      if (article) return article.unit;
    }
    return watchedLines?.[index]?.unit || 'pcs';
  };

  // Real-time totals
  const lineInputs = (watchedLines ?? []).map((l) => ({
    quantity: Number(l.quantity) || 0,
    unitPriceHt: Number(l.unitPriceHt) || 0,
    discountPct: Number(l.discountPct) || 0,
    vatRate: Number(l.vatRate) || 0,
  }));
  const stampApplied = isFiscalStampRequired || watchedStampApplied;
  const stampAmount = stampApplied ? STAMP_AMOUNT : 0;
  const totals = calculateDocumentTotals(
    lineInputs,
    selectedClient?.type ?? 'individual',
    { threshold: 1000, rate: 1 },
    stampAmount,
  );

  const benefitSummary = calculateSalesDocumentBenefit((watchedLines ?? []).map((l, i) => ({
    articleId: l.articleId,
    quantity: Number(l.quantity) || 0,
    unitPriceHt: Number(l.unitPriceHt) || 0,
    discountPct: Number(l.discountPct) || 0,
    unitCostHt: getLineNetCost(i),
  })));
  const totalBenefitRounded = benefitSummary.totalBenefit;
  const hasKnownBenefit = benefitSummary.hasKnownPurchasePrice;

  const handleClientSelect = (client: Client) => {
    setValue('clientId', client.id, { shouldValidate: true });
  };

  const handleArticleSelectFromPicker = (article: Article) => {
    if (articlePickerOpen === null) return;

    if (articlePickerOpen === 'new') {
      // Check if article already exists — increment qty
      const existingIndex = (watchedLines ?? []).findIndex((l) => l.articleId === article.id);
      if (existingIndex >= 0) {
        const currentQty = Number(watchedLines?.[existingIndex]?.quantity) || 0;
        setValue(`lines.${existingIndex}.quantity`, currentQty + 1);
      } else {
        append({
          articleId: article.id,
          description: article.name,
          quantity: 1,
          unitPriceHt: article.salePriceHt,
          discountPct: 0,
          vatRate: article.vatRate,
          unit: article.unit,
          purchasePrice: netUnitCostHt(article) ?? 0,
        });
      }
    } else {
      // Replacing article on a specific line
      const index = articlePickerOpen;
      setValue(`lines.${index}.articleId`, article.id);
      setValue(`lines.${index}.description`, article.name);
      setValue(`lines.${index}.unitPriceHt`, article.salePriceHt);
      setValue(`lines.${index}.vatRate`, article.vatRate);
      setValue(`lines.${index}.unit`, article.unit);
      setValue(`lines.${index}.purchasePrice`, netUnitCostHt(article) ?? 0);
    }
  };

  const handleMultiArticleSelect = (items: ArticleWithQty[]) => {
    for (const { article, quantity } of items) {
      // Check if article already exists in current lines — increment qty
      const existingIndex = (watchedLines ?? []).findIndex((l) => l.articleId === article.id);
      if (existingIndex >= 0) {
        const currentQty = Number(watchedLines?.[existingIndex]?.quantity) || 0;
        setValue(`lines.${existingIndex}.quantity`, currentQty + quantity);
      } else {
        append({
          articleId: article.id,
          description: article.name,
          quantity,
          unitPriceHt: article.salePriceHt,
          discountPct: 0,
          vatRate: article.vatRate,
          unit: article.unit,
          purchasePrice: netUnitCostHt(article) ?? 0,
        });
      }
    }
  };

  // ── Barcode scanner (POS mode) ──────────────────────────────
  const handleBarcodeScan = useCallback((article: Article) => {
    const currentLines = watchedLines ?? [];
    const existingIndex = currentLines.findIndex((l) => l.articleId === article.id);
    if (existingIndex >= 0) {
      const currentQty = Number(currentLines[existingIndex]?.quantity) || 0;
      setValue(`lines.${existingIndex}.quantity`, currentQty + 1);
    } else {
      append({
        articleId: article.id,
        description: article.name,
        quantity: 1,
        unitPriceHt: article.salePriceHt,
        discountPct: 0,
        vatRate: article.vatRate,
        unit: article.unit,
        purchasePrice: netUnitCostHt(article) ?? 0,
      });
    }
  }, [watchedLines, setValue, append]);

  const { scanFeedback } = useBarcodeScanner(articles, handleBarcodeScan, {
    enabled: articlePickerOpen === null,
  });

  const onSubmit = async (data: SalesDocumentFormData) => {
    const linesWithTotals = data.lines.map((l, i) => {
      const lineInput = {
        quantity: Number(l.quantity),
        unitPriceHt: Number(l.unitPriceHt),
        discountPct: Number(l.discountPct),
        vatRate: Number(l.vatRate),
      };
      const lt = calculateLineTotals(lineInput);
      return {
        articleId: l.articleId || undefined,
        description: l.description,
        ...lineInput,
        purchasePrice: getLineNetCost(i),
        totalHt: lt.totalHt,
        totalTtc: lt.totalTtc,
      };
    });

    if (!isExternalCustomer && !data.clientId) {
      setError('clientId', { type: 'manual', message: 'sales.validation.clientRequired' });
      return;
    }
    const client = isExternalCustomer ? undefined : clients?.find((c: Client) => c.id === data.clientId);
    const docStampAmount = (isFiscalStampRequired || data.stampApplied) ? STAMP_AMOUNT : 0;
    const docTotals = calculateDocumentTotals(
      linesWithTotals,
      client?.type ?? 'individual',
      { threshold: 1000, rate: 1 },
      docStampAmount,
    );

    const parentDocIds = document?.parentDocIds?.length
      ? document.parentDocIds
      : data.parentDocId ? [data.parentDocId] : undefined;

    const input = {
      docType,
      customerSource: document?.customerSource ?? 'local',
      clientId: isExternalCustomer ? null : data.clientId,
      onlineCustomerId: document?.onlineCustomerId ?? undefined,
      proCustomerId: document?.proCustomerId ?? undefined,
      proClientId: document?.proClientId ?? undefined,
      representativeId: isExternalCustomer ? null : data.representativeId || null,
      carRegistrationNumber: companySettings?.showClientCarNumber === false
        ? undefined
        : (data.carRegistrationNumber.trim() || null),
      docDate: data.docDate,
      validityDate: data.validityDate || undefined,
      parentDocId: data.parentDocId || undefined,
      parentDocIds,
      proCommandeCode: document?.proCommandeCode ?? undefined,
      notes: data.notes || undefined,
      lines: linesWithTotals,
      ...docTotals,
    };

    if (isEdit && document) {
      await updateMutation.mutateAsync({ id: document.id, input });
    } else {
      const created = await createMutation.mutateAsync(input);

      // Bon de livraison → créer mouvements de stock (sortie)
      if (docType === 'delivery') {
        const defaultWarehouse = warehouses?.find((w) => w.isDefault) ?? warehouses?.[0];
        if (defaultWarehouse) {
          const articleLines = linesWithTotals.filter((l) => l.articleId);
          await Promise.all(articleLines.map((l) =>
            stockMovementMutation.mutateAsync({
              articleId: l.articleId!,
              warehouseId: defaultWarehouse.id,
              movementType: 'out',
              quantity: l.quantity,
              referenceType: 'sales_delivery',
              referenceId: created.id,
              movementDate: data.docDate,
            }),
          ));
        }

        // Auto-create payment depending on user choice
        if (!isExternalCustomer && data.paymentChoice !== 'unpaid') {
          const fullAmount = docTotals.netToPay;
          const amount = data.paymentChoice === 'paid'
            ? fullAmount
            : Math.min(Number(data.paidAmount) || 0, fullAmount);
          if (amount > 0) {
            try {
              await createPaymentMutation.mutateAsync({
                paymentType: 'receipt',
                paymentMethod: data.paymentMethod,
                thirdPartyType: 'client',
                thirdPartyId: data.clientId,
                amount,
                paymentDate: data.docDate,
                allocations: [{ documentType: 'delivery', documentId: created.id, allocatedAmount: amount }],
              });
            } catch (err) {
              // Ne pas bloquer l'enregistrement du doc si le paiement auto échoue :
              // le BL est créé, l'utilisateur peut ajouter le paiement à la main.
              // eslint-disable-next-line no-console
              console.error('[auto-payment] failed for doc', created.id, err);
            }
          }
        }
      }

      if (docType === 'invoice' && parentDocIds?.length) {
        await Promise.all(parentDocIds.map((id) => markInvoicedMutation.mutateAsync(id)));
      }
    }
    const cleanedCarNumber = watchedCarRegistrationNumber?.trim();
    if (
      companySettings?.showClientCarNumber !== false &&
      cleanedCarNumber &&
      !isExternalCustomer &&
      selectedClient &&
      selectedClient.carRegistrationNumber !== cleanedCarNumber
    ) {
      await updateClientMutation.mutateAsync({
        id: selectedClient.id,
        input: { carRegistrationNumber: cleanedCarNumber },
      });
    }
    onSaved();
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement)?.tagName !== 'TEXTAREA') e.preventDefault(); }} className="space-y-5 md:space-y-6 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0">
        {/* Header */}
        <div className="grid grid-cols-1 gap-3 md:gap-4 md:grid-cols-3">
          {!isExternalCustomer ? (
            <div className="space-y-1.5">
              <Label>{t('sales.client')} *</Label>
              {selectedClient ? (
                <div
                  className="flex h-11 md:h-8 items-center justify-between rounded-lg border px-3 cursor-pointer hover:bg-accent transition-colors"
                  onClick={() => setClientPickerOpen(true)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-muted-foreground text-sm">{selectedClient.code}</span>
                    <span className="truncate text-sm font-medium">{selectedClient.name}</span>
                    <Badge
                      variant="outline"
                      className={`shrink-0 text-[10px] font-medium ${selectedClient.type === 'company'
                        ? 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-400'
                        : 'text-muted-foreground'}`}
                    >
                      {selectedClient.type === 'company' ? t('clients.typeCompany') : t('clients.typeIndividual')}
                    </Badge>
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-7 md:h-5 px-2 md:px-1.5 text-xs shrink-0" tabIndex={-1}>
                    {t('sales.picker.changeClient')}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start text-muted-foreground font-normal h-11 md:h-8"
                  onClick={() => setClientPickerOpen(true)}
                >
                  <Search size={14} className="me-2" />
                  {t('sales.picker.chooseClient')}
                </Button>
              )}
              {errors.clientId && <p className="text-xs text-destructive">{t(errors.clientId.message!)}</p>}
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>{t('sales.client')}</Label>
              <Card className="border-dashed bg-muted/20">
                <CardContent className="p-3 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className="shrink-0">
                      {documentCustomer
                        ? t(documentCustomer.sourceLabelKey, documentCustomer.sourceLabel)
                        : t('sales.customerSource.external', 'Source externe')}
                    </Badge>
                    {document?.proCommandeCode && (
                      <span className="text-xs font-mono text-muted-foreground">{document.proCommandeCode}</span>
                    )}
                  </div>
                  <p className="text-sm font-medium">{documentCustomer?.name ?? t('sales.customerSource.externalCustomer', 'Client externe')}</p>
                  {documentCustomer?.secondaryName && (
                    <p className="text-xs text-muted-foreground">{documentCustomer.secondaryName}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {[documentCustomer?.phone, documentCustomer?.email].filter(Boolean).join(' - ') || '-'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {[documentCustomer?.address, documentCustomer?.city].filter(Boolean).join(', ') || '-'}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{t('sales.docDate')} *</Label>
            <Controller control={control} name="docDate" render={({ field }) => (
              <DatePicker value={field.value} onChange={field.onChange} />
            )} />
          </div>
          {docType === 'quote' && (
            <div className="space-y-1.5">
              <Label>{t('sales.validityDate')}</Label>
              <Controller control={control} name="validityDate" render={({ field }) => (
                <DatePicker value={field.value} onChange={field.onChange} />
              )} />
            </div>
          )}
          {isEdit && document?.number && (
            <div className="space-y-1.5">
              <Label>{t('sales.number')}</Label>
              <div className="flex h-11 md:h-8 items-center rounded-lg border bg-muted/40 px-3 text-sm font-medium">
                {document.number}
              </div>
            </div>
          )}
          {selectedClient && (
            <div className="space-y-1.5">
              <Label>{t('representatives.representative')}</Label>
              <Controller
                control={control}
                name="representativeId"
                render={({ field }) => (
                  <RepresentativeSelect
                    value={watchedRepId || null}
                    onChange={(id) => field.onChange(id ?? '')}
                    preferredIds={preferredRepIds}
                    placeholder={t('representatives.pick')}
                  />
                )}
              />
            </div>
          )}
          {companySettings?.showClientCarNumber !== false && selectedClient && (
            <div className="space-y-1.5">
              <Label htmlFor="carRegistrationNumber">{t('sales.carRegistrationNumber')}</Label>
              <Input
                id="carRegistrationNumber"
                {...register('carRegistrationNumber')}
                placeholder={t('sales.carRegistrationPlaceholder')}
              />
            </div>
          )}
        </div>

        <Separator />

        {/* Lines */}
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-base font-semibold">{t('sales.lines')}</Label>
            <div className="hidden md:block">
              <ColumnConfigButton onClick={() => setLineColDialogOpen(true)} />
            </div>
          </div>

          {errors.lines?.root && (
            <p className="text-xs text-destructive">{t(errors.lines.root.message!)}</p>
          )}

          {/* ── MOBILE: stacked card layout ─────────────────── */}
          <div className="md:hidden space-y-3">
            {fields.length === 0 && (
              <div className="rounded-lg border border-dashed bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
                {t('sales.picker.addArticle')}
              </div>
            )}
            {fields.map((field, index) => {
              const qty = Number(watchedLines?.[index]?.quantity) || 0;
              const price = Number(watchedLines?.[index]?.unitPriceHt) || 0;
              const disc = Number(watchedLines?.[index]?.discountPct) || 0;
              const costPrice = getLineNetCost(index);
              const unit = getLineUnit(index);
              const lineHt = Math.round((qty * price * (1 - disc / 100)) * 1000) / 1000;
              const lineArticleId = watchedLines?.[index]?.articleId;
              const lineBenefit = calculateSalesLineBenefit({
                articleId: lineArticleId,
                quantity: qty,
                unitPriceHt: price,
                discountPct: disc,
                unitCostHt: costPrice,
              });

              return (
                <div key={field.id} className="rounded-xl border bg-card p-3 space-y-3 shadow-xs">
                  {/* Top: article search + description + delete */}
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="icon-sm" className="h-10 w-10 shrink-0"
                      onClick={() => setArticlePickerOpen(index)} aria-label={lineArticleId ? t('sales.picker.changeArticle') : t('sales.picker.chooseArticle')}>
                      <Search size={16} />
                    </Button>
                    <Input {...register(`lines.${index}.description`)}
                      placeholder={t('sales.picker.chooseArticle')}
                      readOnly={!!lineArticleId}
                      onClick={lineArticleId ? () => setArticlePickerOpen(index) : undefined}
                      title={lineArticleId ? t('sales.picker.changeArticle') : undefined}
                      className={`h-10 text-sm flex-1 ${lineArticleId ? 'cursor-pointer caret-transparent bg-muted/40' : ''}`} />
                    {lineArticleId && (
                      <Button type="button" variant="ghost" size="icon-sm" className="h-10 w-10 shrink-0 text-muted-foreground"
                        onClick={() => setViewingArticleId(lineArticleId)} aria-label={t('common.view')}>
                        <Eye size={16} />
                      </Button>
                    )}
                    <Button type="button" variant="ghost" size="icon-sm" className="h-10 w-10 shrink-0 text-muted-foreground"
                      onClick={() => remove(index)} aria-label={t('common.delete')}>
                      <Trash2 size={16} />
                    </Button>
                  </div>

                  {/* Qty + Unit Price */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground font-normal">{t('sales.quantity')}</Label>
                      <div className="flex items-center gap-1">
                        <Button type="button" variant="outline" size="icon-sm" className="h-10 w-10 shrink-0"
                          onClick={() => { if (qty > 1) setValue(`lines.${index}.quantity`, qty - 1); }}
                          aria-label="-"><Minus size={14} /></Button>
                        <Input type="number" inputMode="decimal" step={getQtyStep(unit)} min={INTEGER_UNITS.has(unit) ? '1' : '0.001'}
                          {...register(`lines.${index}.quantity`, { valueAsNumber: true })}
                          className="text-center px-1 h-10 flex-1 min-w-0" />
                        <Button type="button" variant="outline" size="icon-sm" className="h-10 w-10 shrink-0"
                          onClick={() => setValue(`lines.${index}.quantity`, qty + 1)}
                          aria-label="+"><Plus size={14} /></Button>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground font-normal">{t('sales.unitPrice')}</Label>
                      <Input type="number" inputMode="decimal" step="0.001"
                        {...register(`lines.${index}.unitPriceHt`, { valueAsNumber: true })}
                        className="h-10 text-sm" />
                    </div>
                  </div>

                  {/* Discount + VAT */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground font-normal">{t('sales.discount')}</Label>
                      <Input type="number" inputMode="decimal" step="0.01"
                        {...register(`lines.${index}.discountPct`, { valueAsNumber: true })}
                        className="h-10 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground font-normal">{t('sales.vatRate')}</Label>
                      <Controller control={control} name={`lines.${index}.vatRate`}
                        render={({ field: f }) => (
                          <Select value={String(f.value ?? 19)} onValueChange={(v) => f.onChange(Number(v))}>
                            <SelectTrigger className="h-10 w-full"><SelectValue /></SelectTrigger>
                            <SelectContent>{VAT_RATES.map((r) => (<SelectItem key={r} value={String(r)}>{r}%</SelectItem>))}</SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  </div>

                  {/* Readonly totals block */}
                  <div className="rounded-lg bg-muted/50 px-3 py-2 space-y-1 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground text-xs uppercase tracking-wide">{t('sales.lineTotal')}</span>
                      <span className="font-semibold">{formatPrice(lineHt)} DT</span>
                    </div>
                    {lineBenefit.hasKnownPurchasePrice && (
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground text-xs uppercase tracking-wide">{t('sales.benefit')}</span>
                        <span className={`font-medium ${lineBenefit.benefit >= 0 ? 'text-green-600' : 'text-destructive'}`}>
                          {lineBenefit.benefit >= 0 ? '+' : ''}{formatPrice(lineBenefit.benefit)} DT
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── DESKTOP: grid layout ────────────────────────── */}
          <div className="hidden md:block">
            {/* Column headers */}
            <div className="grid gap-3 text-[11px] font-medium text-muted-foreground px-2 pb-2" style={lineGridStyle}>
              {lineColCfg.visibleCols.map((key) => {
                const col = SALES_LINE_COL_DEFS.find((c) => c.key === key);
                if (!col) return null;
                const align = (key === 'unitPriceTtc' || key === 'purchasePrice' || key === 'lineTotal' || key === 'lineTotalTtc' || key === 'benefit')
                  ? 'text-end'
                  : key === 'lineNumber' || key === 'quantity' || key === 'unit'
                    ? 'text-center'
                    : '';
                return <div key={key} className={align}>{t(col.labelKey)}</div>;
              })}
              <div className="w-7" />
            </div>

            <div className="divide-y rounded-lg border bg-card">
              {fields.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {t('sales.picker.addArticle')}
                </div>
              )}
              {fields.map((field, index) => {
                const qty = Number(watchedLines?.[index]?.quantity) || 0;
                const price = Number(watchedLines?.[index]?.unitPriceHt) || 0;
                const disc = Number(watchedLines?.[index]?.discountPct) || 0;
                const costPrice = getLineNetCost(index);
                const unit = getLineUnit(index);
                const lineHt = Math.round((qty * price * (1 - disc / 100)) * 1000) / 1000;
                const lineTtc = Math.round((lineHt * (1 + (Number(watchedLines?.[index]?.vatRate) || 0) / 100)) * 1000) / 1000;
                const lineArticleId = watchedLines?.[index]?.articleId;
                const lineArticle = lineArticleId ? articleMap.get(lineArticleId) : undefined;
                const lineBenefit = calculateSalesLineBenefit({
                  articleId: lineArticleId,
                  quantity: qty,
                  unitPriceHt: price,
                  discountPct: disc,
                  unitCostHt: costPrice,
                });

                return (
                  <div
                    key={field.id}
                    className="grid gap-3 items-center px-2 py-3"
                    style={lineGridStyle}
                  >
                    {lineColCfg.visibleCols.map((key) => {
                      switch (key) {
                        case 'lineNumber':
                          return <span key={key} className="text-sm text-muted-foreground text-center">{index + 1}</span>;
                        case 'articleCode':
                          return <span key={key} className="truncate text-sm">{lineArticle?.code ?? '—'}</span>;
                        case 'description':
                          return (
                            <div key={key} className="flex items-center gap-2 min-w-0">
                              <Button type="button" variant="ghost" size="icon-xs" className="shrink-0 text-muted-foreground hover:text-primary" onClick={() => setArticlePickerOpen(index)} aria-label={lineArticleId ? t('sales.picker.changeArticle') : t('sales.picker.chooseArticle')}>
                                <Search size={14} />
                              </Button>
                              <Input {...register(`lines.${index}.description`)}
                                placeholder={t('sales.picker.chooseArticle')}
                                readOnly={!!lineArticleId}
                                onClick={lineArticleId ? () => setArticlePickerOpen(index) : undefined}
                                title={lineArticleId ? t('sales.picker.changeArticle') : undefined}
                                className={`h-8 text-sm flex-1 ${lineArticleId ? 'cursor-pointer caret-transparent bg-muted/40' : ''}`} />
                            </div>
                          );
                        case 'quantity':
                          return (
                            <div key={key} className="flex items-center gap-0.5 justify-center">
                              <Button type="button" variant="outline" size="icon-xs" className="h-8 w-7 shrink-0"
                                onClick={() => { if (qty > 1) setValue(`lines.${index}.quantity`, qty - 1); }}><Minus size={10} /></Button>
                              <Input type="number" step={getQtyStep(unit)} min={INTEGER_UNITS.has(unit) ? '1' : '0.001'}
                                {...register(`lines.${index}.quantity`, { valueAsNumber: true })} className="text-center px-0.5 h-8 w-14" />
                              <Button type="button" variant="outline" size="icon-xs" className="h-8 w-7 shrink-0"
                                onClick={() => setValue(`lines.${index}.quantity`, qty + 1)}><Plus size={10} /></Button>
                            </div>
                          );
                        case 'unit':
                          return <span key={key} className="text-sm text-center">{unit}</span>;
                        case 'unitPrice':
                          return <Input key={key} type="number" step="0.001" {...register(`lines.${index}.unitPriceHt`, { valueAsNumber: true })} className="h-8 text-sm" />;
                        case 'unitPriceTtc':
                          return <span key={key} className="text-sm text-end">{formatPrice(price * (1 + (Number(watchedLines?.[index]?.vatRate) || 0) / 100))}</span>;
                        case 'purchasePrice':
                          return <span key={key} className="text-sm text-end">{lineArticleId && costPrice > 0 ? formatPrice(costPrice) : '—'}</span>;
                        case 'discount':
                          return <Input key={key} type="number" step="0.01" {...register(`lines.${index}.discountPct`, { valueAsNumber: true })} className="h-8 text-sm" />;
                        case 'vatRate':
                          return (
                            <Controller key={key} control={control} name={`lines.${index}.vatRate`}
                              render={({ field: f }) => (
                                <Select value={String(f.value ?? 19)} onValueChange={(v) => f.onChange(Number(v))}>
                                  <SelectTrigger className="h-8 w-full"><SelectValue /></SelectTrigger>
                                  <SelectContent>{VAT_RATES.map((r) => (<SelectItem key={r} value={String(r)}>{r}%</SelectItem>))}</SelectContent>
                                </Select>
                              )}
                            />
                          );
                        case 'lineTotal':
                          return <span key={key} className="text-sm font-semibold text-end">{formatPrice(lineHt)}</span>;
                        case 'lineTotalTtc':
                          return <span key={key} className="text-sm font-semibold text-end">{formatPrice(lineTtc)}</span>;
                        case 'benefit':
                          return (
                            <span key={key} className="text-end text-sm">
                              {lineBenefit.hasKnownPurchasePrice ? (
                                <span className={lineBenefit.benefit >= 0 ? 'text-green-600' : 'text-destructive'}>
                                  {lineBenefit.benefit >= 0 ? '+' : ''}{formatPrice(lineBenefit.benefit)}
                                </span>
                              ) : <span className="text-muted-foreground">—</span>}
                            </span>
                          );
                        default:
                          return null;
                      }
                    })}
                    <div className="flex items-center gap-0.5 justify-self-end">
                      {lineArticleId && (
                        <Button type="button" variant="ghost" size="icon-xs" className="w-7 h-8 text-muted-foreground hover:text-primary"
                          onClick={() => setViewingArticleId(lineArticleId)} aria-label={t('common.view')}>
                          <Eye size={14} />
                        </Button>
                      )}
                      <Button type="button" variant="ghost" size="icon-xs" className="w-7 h-8" onClick={() => remove(index)} aria-label={t('common.delete')}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:flex sm:justify-end gap-2">
            <Button type="button" variant="default" size="sm" className="h-10 sm:h-7" onClick={() => setArticlePickerOpen('new')}>
              <PackageSearch size={14} className="me-1.5" />
              {t('sales.picker.addArticle')}
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-10 sm:h-7" onClick={() => append(defaultLine)}>
              <Plus size={14} className="me-1.5" />
              {t('sales.addLine')}
            </Button>
          </div>
        </div>

        <Separator />

        {/* Totals + payment (delivery) */}
        <div className="flex justify-end">
          <div className="w-full md:max-w-sm space-y-3">
            <div className="rounded-lg md:rounded-none border md:border-0 bg-muted/30 md:bg-transparent p-3 md:p-0 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.totalHt')}</span>
                {formatPrice(totals.totalHt)} DT
              </div>
              {totals.totalDiscount > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.totalDiscount')}</span>
                  -{formatPrice(totals.totalDiscount)} DT
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('sales.totalVat')}</span>
                {formatPrice(totals.totalVat)} DT
              </div>
              <Separator />
              <div className="flex justify-between font-medium">
                <span>{t('sales.totalTtc')}</span>
                {formatPrice(totals.totalTtc)} DT
              </div>
              {totals.withholdingAmount > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>{t('sales.withholding')}</span>
                  -{formatPrice(totals.withholdingAmount)} DT
                </div>
              )}
              <Controller
                control={control}
                name="stampApplied"
                render={({ field }) => (
                  <div className="flex items-center justify-between pt-1">
                    {isFiscalStampRequired ? (
                      <span className="text-muted-foreground">{t('sales.stamp.label')}</span>
                    ) : (
                      <Button
                        type="button"
                        variant={field.value ? 'default' : 'outline'}
                        size="sm"
                        className="h-8"
                        onClick={() => field.onChange(!field.value)}
                      >
                        {field.value ? t('sales.stamp.remove') : t('sales.stamp.add')}
                      </Button>
                    )}
                    {(isFiscalStampRequired || field.value) && <span className="font-medium">{formatPrice(STAMP_AMOUNT)} DT</span>}
                  </div>
                )}
              />
              {(totals.withholdingAmount > 0 || totals.stampAmount > 0) && (
                <>
                  <Separator />
                  <div className="flex justify-between font-semibold">
                    <span>{t('sales.netToPay')}</span>
                    {formatPrice(totals.netToPay)} DT
                  </div>
                </>
              )}
              {hasKnownBenefit && (
                <>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('sales.benefit')}</span>
                    <span className={`font-medium ${totalBenefitRounded >= 0 ? 'text-green-600' : 'text-destructive'}`}>
                      {totalBenefitRounded >= 0 ? '+' : ''}{formatPrice(totalBenefitRounded)} DT
                    </span>
                  </div>
                </>
              )}
            </div>

            {docType === 'delivery' && !isEdit && (
              <Card size="sm" className="p-2 gap-2">
                <Controller
                  control={control}
                  name="paymentChoice"
                  render={({ field }) => (
                    <div className="grid grid-cols-3 gap-0.5 rounded-md border bg-background p-0.5">
                      {(['paid', 'partial', 'unpaid'] as const).map((opt) => {
                        const active = field.value === opt;
                        const activeStyles =
                          opt === 'paid'
                            ? 'bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-400'
                            : opt === 'partial'
                              ? 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/15 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-400'
                              : 'bg-rose-500/15 text-rose-700 hover:bg-rose-500/15 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-400';
                        return (
                          <Button
                            key={opt}
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={() => field.onChange(opt)}
                            className={`h-7 px-2 text-xs ${active ? activeStyles : 'text-muted-foreground'}`}
                          >
                            {t(`payments.choice.${opt}`)}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                />
                {watchedPaymentChoice !== 'unpaid' && (
                  <div className="flex gap-2">
                    <Controller
                      control={control}
                      name="paymentMethod"
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {(['cash', 'transfer', 'check', 'card'] as const).map((m) => (
                              <SelectItem key={m} value={m}>{t(`payments.methods.${m}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {watchedPaymentChoice === 'partial' && (
                      <Input
                        type="number"
                        step="0.001"
                        placeholder={t('payments.amount')}
                        {...register('paidAmount', { valueAsNumber: true })}
                        className="h-8 w-28 text-xs"
                      />
                    )}
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>

        {/* Payment section — edit BL: list payments + add */}
        {docType === 'delivery' && isEdit && document && (
          <Card size="sm" className="space-y-3 p-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">{t('payments.section')}</Label>
              {(() => {
                const allocated = (docPayments ?? []).reduce(
                  (s, p) => s + sumAllocations(p.allocations?.filter((a) => a.documentId === document.id)),
                  0,
                );
                const status: PaymentStatus = computePaymentStatus(document.netToPay ?? document.totalTtc, allocated);
                return <PaymentStatusBadge status={status} />;
              })()}
            </div>
            <div className="space-y-2 text-sm">
              {(docPayments ?? []).length === 0 && (
                <p className="text-muted-foreground">{t('payments.noPayments')}</p>
              )}
              {(docPayments ?? []).map((p) => {
                const alloc = sumAllocations(p.allocations?.filter((a) => a.documentId === document.id));
                return (
                  <div key={p.id} className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
                    <div>
                      <span className="font-medium">{p.number}</span>
                      <span className="text-muted-foreground ms-2">{t(`payments.methods.${p.paymentMethod}`)}</span>
                      <span className="text-muted-foreground ms-2">{p.paymentDate}</span>
                    </div>
                    <span className="font-semibold">{formatPrice(alloc)} DT</span>
                  </div>
                );
              })}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setAddPaymentOpen(true)}>
              <Plus size={14} className="me-1.5" />
              {t('payments.addPayment')}
            </Button>
          </Card>
        )}

        {/* Notes */}
        <div className="space-y-1.5">
          <Label htmlFor="notes">{t('sales.notes')}</Label>
          <Textarea id="notes" {...register('notes')} rows={2} />
        </div>

        {/* Desktop actions */}
        <div className="hidden md:flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('common.loading') : t('common.save')}
          </Button>
        </div>

        {/* Mobile sticky action bar */}
        <div className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex gap-2">
          <Button type="button" variant="outline" className="flex-1 h-11" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" className="flex-[2] h-11" disabled={isSubmitting}>
            {isSubmitting ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </form>

      <ClientPickerDialog
        open={clientPickerOpen}
        onOpenChange={setClientPickerOpen}
        onSelect={handleClientSelect}
      />
      <ArticlePickerDialog
        open={articlePickerOpen !== null}
        onOpenChange={(open) => { if (!open) setArticlePickerOpen(null); }}
        onSelect={handleArticleSelectFromPicker}
        onSelectMultiple={handleMultiArticleSelect}
      />
      <ArticleFormDialog
        open={viewingArticleId !== null}
        onOpenChange={(open) => { if (!open) setViewingArticleId(null); }}
        article={viewingArticleId ? articleMap.get(viewingArticleId) ?? null : null}
      />

      {isEdit && document && docType === 'delivery' && (
        <AddPaymentDialog
          open={addPaymentOpen}
          onOpenChange={setAddPaymentOpen}
          documentId={document.id}
          documentType="delivery"
          clientId={document.clientId}
          remainingAmount={Math.max(
            0,
            (document.netToPay ?? document.totalTtc) -
              (docPayments ?? []).reduce(
                (s, p) => s + sumAllocations(p.allocations?.filter((a) => a.documentId === document.id)),
                0,
              ),
          )}
        />
      )}

      {/* Barcode scan feedback toast */}
      {scanFeedback && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium animate-in fade-in slide-in-from-bottom-4 duration-200 ${
          scanFeedback.type === 'success'
            ? 'bg-success text-success-foreground'
            : 'bg-destructive text-destructive-foreground'
        }`}>
          {scanFeedback.text}
        </div>
      )}

      <ColumnConfigDialog
        open={lineColDialogOpen}
        onOpenChange={setLineColDialogOpen}
        visibleCols={lineColCfg.visibleCols}
        allColumns={lineColCfg.allColumns}
        toggleCol={lineColCfg.toggleCol}
        moveCol={lineColCfg.moveCol}
        resetCols={lineColCfg.resetCols}
      />
    </>
  );
}
